import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { Mail, Phone, MapPin, Send, Clock, Facebook, Instagram, Linkedin, Globe, Youtube } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { toast } from '../hooks/use-toast';
import { mockData } from '../data/mock';
import OptimizedImage from '../components/OptimizedImage';

const Contact = () => {
  const { company } = mockData;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      toast({
        title: 'Email service not configured',
        description: 'Please set REACT_APP_EMAILJS_* keys in frontend/.env and restart.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone_number: formData.phone,
          event_type: formData.eventType,
          message: formData.message,
        },
        publicKey
      );

      toast({
        title: 'Message sent successfully',
        description: 'Thank you! We will get back to you within 24 hours.'
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        eventType: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: 'Failed to send message',
        description: 'Please try again in a moment or email us directly.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="bg-dark-bg">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="/bonfire.jpg"
            alt="Contact Us"
            className="w-full h-full object-cover"
            priority={true}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6 lg:px-12 text-center pt-32 pb-20">
          <p className="text-gold text-sm tracking-widest uppercase mb-4">Get In Touch</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-silver leading-tight">
            Let's Start
            <span className="gold-gradient-text block mt-2">Planning Together</span>
          </h1>
          {/* Golden Decorative Line */}
          <div className="flex justify-center mt-8">
            <div className="relative w-96 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent shadow-lg" style={{
              boxShadow: '0 0 15px rgba(255, 215, 0, 0.4), 0 0 30px rgba(255, 215, 0, 0.2)'
            }}>
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gold rounded-full shadow-lg" style={{
                boxShadow: '0 0 10px rgba(255, 215, 0, 0.6), 0 0 20px rgba(255, 215, 0, 0.3)'
              }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-32 bg-gradient-to-b from-black to-dark-grey">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div className="space-y-4">
                <h2 className="text-4xl md:text-5xl font-bold text-silver leading-tight">
                  Contact
                  <span className="gold-gradient-text"> Information</span>
                </h2>
                <p className="text-gray-300 text-lg leading-relaxed">
                  Ready to bring your event vision to life? Reach out to us and let's create something extraordinary together.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                <Card className="bg-dark-grey border-gold/20 hover:border-gold transition-all duration-300 group">
                  <div className="p-6 flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                      <Phone className="text-gold" size={20} />
                    </div>
                    <div>
                      <h4 className="text-silver font-semibold mb-1">Phone</h4>
                      <p className="text-gray-300 text-sm">{company.contact.phone}</p>
                    </div>
                  </div>
                </Card>

                <Card className="bg-dark-grey border-gold/20 hover:border-gold transition-all duration-300 group">
                  <div className="p-6 flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                      <Mail className="text-gold" size={20} />
                    </div>
                    <div>
                      <h4 className="text-silver font-semibold mb-1">Email</h4>
                      <p className="text-gray-300 text-sm break-all">{company.contact.email}</p>
                    </div>
                  </div>
                </Card>

                <Card className="bg-dark-grey border-gold/20 hover:border-gold transition-all duration-300 group">
                  <div className="p-6 flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                      <MapPin className="text-gold" size={20} />
                    </div>
                    <div>
                      <h4 className="text-silver font-semibold mb-1">Address</h4>
                      <p className="text-gray-300 text-sm">{company.contact.address}</p>
                    </div>
                  </div>
                </Card>

                <Card className="bg-dark-grey border-gold/20 hover:border-gold transition-all duration-300 group">
                  <div className="p-6 flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                      <Clock className="text-gold" size={20} />
                    </div>
                    <div>
                      <h4 className="text-silver font-semibold mb-1">Office Hours</h4>
                      <p className="text-gray-300 text-sm">Mon - Fri: 9:00 AM - 6:00 PM</p>
                      <p className="text-gray-300 text-sm">Sat: 10:00 AM - 4:00 PM</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Indian Counterpart */}
              <div className="space-y-4 pt-4 border-t border-gold/20">
                <div>
                  <h4 className="text-silver font-semibold mb-2">Our Indian Counterpart</h4>
                  <div className="flex items-center gap-3 mb-4">
                    <OptimizedImage
                      src="/likewize_logo.png"
                      alt="LikeWize"
                      className="h-12 w-auto object-contain"
                      loading="lazy"
                    />
                    <p className="text-gold text-lg font-bold">LikeWize</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Phone className="text-gold" size={16} />
                    <a href="tel:+919870409708" className="text-gray-300 text-sm hover:text-gold transition-colors">
                      +91 9870409708
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="text-gold" size={16} />
                    <a href="tel:+919820480705" className="text-gray-300 text-sm hover:text-gold transition-colors">
                      +91 9820480705
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="text-gold" size={16} />
                    <a href="mailto:fenil@likewize.co.in" className="text-gray-300 text-sm hover:text-gold transition-colors break-all">
                      fenil@likewize.co.in
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Globe className="text-gold" size={16} />
                    <a href="https://likewize.co.in/" target="_blank" rel="noopener noreferrer" className="text-gray-300 text-sm hover:text-gold transition-colors">
                      www.likewize.co.in
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <a
                    href="https://likewize.co.in/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-dark-grey border border-gold/20 hover:border-gold flex items-center justify-center transition-all duration-300 hover:scale-110"
                  >
                    <Globe className="text-gray-400 hover:text-gold" size={18} />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/likewizeindia"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-dark-grey border border-gold/20 hover:border-gold flex items-center justify-center transition-all duration-300 hover:scale-110"
                  >
                    <Linkedin className="text-gray-400 hover:text-gold" size={18} />
                  </a>
                  <a
                    href="https://www.youtube.com/@like_wize"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-dark-grey border border-gold/20 hover:border-gold flex items-center justify-center transition-all duration-300 hover:scale-110"
                  >
                    <Youtube className="text-gray-400 hover:text-gold" size={18} />
                  </a>
                  <a
                    href="https://www.instagram.com/like_wize_14?igsh=MTF4d3NhOGlxczlxcQ%3D%3D&utm_source=qr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-dark-grey border border-gold/20 hover:border-gold flex items-center justify-center transition-all duration-300 hover:scale-110"
                  >
                    <Instagram className="text-gray-400 hover:text-gold" size={18} />
                  </a>
                </div>
              </div>

              {/* Social Media */}
              <div className="space-y-4 pt-4 border-t border-gold/20">
                <h4 className="text-silver font-semibold">Follow Us</h4>
                <div className="flex items-center gap-4">
                  <a
                    href={company.contact.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-dark-grey border border-gold/20 hover:border-gold flex items-center justify-center transition-all duration-300 hover:scale-110"
                  >
                    <Facebook className="text-gray-400 hover:text-gold" size={20} />
                  </a>
                  <a
                    href={company.contact.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-dark-grey border border-gold/20 hover:border-gold flex items-center justify-center transition-all duration-300 hover:scale-110"
                  >
                    <Instagram className="text-gray-400 hover:text-gold" size={20} />
                  </a>
                  <a
                    href={company.contact.social.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-dark-grey border border-gold/20 hover:border-gold flex items-center justify-center transition-all duration-300 hover:scale-110"
                  >
                    <Linkedin className="text-gray-400 hover:text-gold" size={20} />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <Card className="bg-dark-grey border-gold/20">
                <div className="p-8 lg:p-12">
                  <div className="mb-8">
                    <h3 className="text-3xl font-bold text-silver mb-2">Send Us a Message</h3>
                    <p className="text-gray-300">Fill out the form below and we'll get back to you within 24 hours.</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-sm text-gray-300 font-medium">
                          Full Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="bg-black border-gold/20 text-silver placeholder:text-gray-500 focus:border-gold rounded-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="email" className="text-sm text-gray-300 font-medium">
                          Email Address *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="bg-black border-gold/20 text-silver placeholder:text-gray-500 focus:border-gold rounded-none"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="phone" className="text-sm text-gray-300 font-medium">
                          Phone Number
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="+66 XX XXX XXXX"
                          value={formData.phone}
                          onChange={handleChange}
                          className="bg-black border-gold/20 text-silver placeholder:text-gray-500 focus:border-gold rounded-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="eventType" className="text-sm text-gray-300 font-medium">
                          Event Type *
                        </label>
                        <select
                          id="eventType"
                          name="eventType"
                          value={formData.eventType}
                          onChange={handleChange}
                          required
                          className="w-full bg-black border border-gold/20 text-silver px-3 py-2 focus:border-gold focus:outline-none rounded-none"
                        >
                          <option value="">Select an event type</option>
                          <option value="wedding">Wedding</option>
                          <option value="corporate">Corporate Event</option>
                          <option value="conference">Conference/MICE</option>
                          <option value="product-launch">Product Launch</option>
                          <option value="gala">Gala Dinner</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="message" className="text-sm text-gray-300 font-medium">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Tell us about your event..."
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="bg-black border-gold/20 text-silver placeholder:text-gray-500 focus:border-gold rounded-none resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      variant="outline"
                      disabled={isSubmitting}
                      className="w-full border-2 border-gold text-silver hover:bg-gold/10 py-4 text-lg rounded-none transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Sending…' : 'Send Message'}
                      <Send size={20} />
                    </Button>
                  </form>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-[500px] bg-black border-t border-gold/20">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3875.5474839!2d100.5404!3d13.7465!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTPCsDQ0JzQ3LjQiTiAxMDDCsDMyJzI1LjQiRQ!5e0!3m2!1sen!2sth!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Prestige Events Location"
        ></iframe>
      </section>
    </div>
  );
};

export default Contact;